import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const TermsConditions = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEO
      title="Terms and Conditions — TruthForge AI"
      description="Terms governing use of the TruthForge AI website and services, provided by TruthForge Limited."
      path="/terms-and-conditions"
    />
    <Navigation />
    <main className="mx-auto max-w-3xl px-5 py-12 md:px-8 md:py-16">
      <div className="mb-10 border-b border-border/70 pb-8">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">TruthForge Limited</p>
        <h1 className="text-3xl font-semibold tracking-normal text-foreground md:text-4xl">Terms and Conditions</h1>
        <p className="mt-4 text-xs uppercase tracking-[0.1em] text-body">Last updated: 28 April 2026</p>
      </div>

      <div className="space-y-10 text-sm [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:tracking-normal [&_h3]:text-base [&_h3]:font-semibold [&_h3]:tracking-normal [&_li]:pl-1 [&_li]:leading-7 [&_p]:leading-7 [&_p]:text-body [&_section]:space-y-4 [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:text-body">
        <section>
          <h2>1. About These Terms</h2>
          <p>These Terms and Conditions ("Terms") govern your use of the TruthForge Limited website at <span className="text-foreground">www.truthforge.ai</span> (the "Site") and the engagement of our Services.</p>
          <p>By accessing the Site or submitting an enquiry, you confirm that you have read, understood, and agree to these Terms.</p>
          <p>TruthForge Limited is a company registered in England and Wales. Our registered office is at The Circle, Queen Elizabeth Street, London, SE1 2JJ.</p>
          <p>If you have any questions about these Terms, contact us at: <strong className="text-foreground">legal@truthforge.ai</strong></p>
        </section>

        <section>
          <h2>2. Our Services</h2>
          <p>TruthForge provides narrative intelligence and decision support services, including:</p>
          <ul>
            <li>Narrative Stress-Testing, modelling how a situation, statement, or action is likely to be interpreted and responded to across stakeholder groups before exposure</li>
            <li>Digital Twin Analysis, building a living model of how an entity exists in collective perception</li>
            <li>Situational Advisory, supporting legal, communications, and leadership teams to align strategy before and during high-stakes situations</li>
          </ul>
          <p>The specific scope of any engagement will be agreed in writing between TruthForge and the client prior to commencement.</p>
          <p><strong className="text-foreground">TruthForge's Services are advisory in nature.</strong> Nothing we provide constitutes legal advice, financial advice, public relations advice, or any other regulated professional service. You remain responsible for all decisions you make, and we strongly recommend you take independent legal and professional advice as appropriate to your situation.</p>
        </section>

        <section>
          <h2>3. Enquiries and Engagement</h2>
          <h3>3.1 Submitting an Enquiry</h3>
          <p>Submitting an enquiry through our Site or contacting us does not create a contractual relationship or any obligation on either party. An engagement only begins when both parties have agreed the scope and terms in writing.</p>
          <h3>3.2 Engagement Terms</h3>
          <p>The specific commercial terms of any engagement, including fees, deliverables, timelines, and confidentiality arrangements, will be set out in a separate engagement letter or statement of work agreed between TruthForge and the client.</p>
          <p>In the event of any conflict between these Terms and an engagement letter, the engagement letter prevails.</p>
          <h3>3.3 Eligibility</h3>
          <p>Our Services are intended for business clients. By engaging TruthForge, you represent that you are acting in a business or professional capacity, not as a consumer.</p>
        </section>

        <section>
          <h2>4. Confidentiality</h2>
          <h3>4.1 Our Obligations</h3>
          <p>We treat all information shared with us in the course of an enquiry or engagement as strictly confidential. We will not disclose, publish, or reference your situation, identity, or any information you share with us to any third party, except:</p>
          <ul>
            <li>With your explicit written consent</li>
            <li>To our employees, contractors, or advisors who need it to deliver the Services, and who are bound by equivalent confidentiality obligations</li>
            <li>Where required by law, court order, or regulatory authority</li>
          </ul>
          <h3>4.2 Your Obligations</h3>
          <p>You agree to keep confidential any methodologies, frameworks, analytical outputs, and proprietary information provided by TruthForge in the course of an engagement.</p>
          <h3>4.3 Survival</h3>
          <p>Confidentiality obligations survive the termination of any engagement for a period of five (5) years.</p>
        </section>

        <section>
          <h2>5. Intellectual Property</h2>
          <h3>5.1 TruthForge IP</h3>
          <p>All intellectual property in TruthForge's methodologies, frameworks, tools, processes, and pre-existing materials remains the exclusive property of TruthForge Limited. Nothing in these Terms or any engagement grants you any ownership of TruthForge IP.</p>
          <h3>5.2 Deliverables</h3>
          <p>Unless otherwise agreed in writing, deliverables produced by TruthForge for a client are licensed (not sold) to that client for their internal use only, following full payment of all fees. You may not reproduce, distribute, or commercialise deliverables without our prior written consent.</p>
          <h3>5.3 Site Content</h3>
          <p>All content on the Site, including text, graphics, logos, and design, is the property of TruthForge Limited or its licensors and is protected by copyright and other intellectual property laws. You may not copy, reproduce, or distribute Site content without our prior written permission.</p>
        </section>

        <section>
          <h2>6. Use of the Site</h2>
          <p>You agree to use the Site only for lawful purposes and in a manner that does not infringe the rights of others. You must not:</p>
          <ul>
            <li>Use the Site in any way that could damage, disable, or impair it</li>
            <li>Attempt to gain unauthorised access to any part of the Site or its underlying systems</li>
            <li>Submit false, misleading, or fraudulent information through any form on the Site</li>
            <li>Use automated tools to scrape, crawl, or extract data from the Site</li>
          </ul>
        </section>

        <section>
          <h2>7. Disclaimer of Warranties</h2>
          <p>The Site and its content are provided on an "as is" basis. To the fullest extent permitted by law, TruthForge makes no warranties, express or implied, regarding the Site, including its accuracy, completeness, fitness for a particular purpose, or uninterrupted availability.</p>
          <p>TruthForge's analytical outputs represent informed assessments based on available information. They are not guarantees of outcome. Real-world situations involve inherent uncertainty, and no analysis can eliminate that uncertainty entirely.</p>
        </section>

        <section>
          <h2>8. Limitation of Liability</h2>
          <h3>8.1 Exclusions</h3>
          <p>To the fullest extent permitted by law, TruthForge will not be liable for:</p>
          <ul>
            <li>Any indirect, consequential, special, or punitive loss or damage</li>
            <li>Loss of profit, revenue, business, or anticipated savings</li>
            <li>Loss of goodwill or reputation</li>
            <li>Any loss arising from your reliance on the Site's content without engaging our Services</li>
          </ul>
          <h3>8.2 Cap on Liability</h3>
          <p>Where TruthForge is liable to you in connection with an engagement, our total liability will not exceed the fees paid by you under that engagement in the three (3) months preceding the event giving rise to the claim.</p>
          <h3>8.3 Exceptions</h3>
          <p>Nothing in these Terms limits or excludes liability for death or personal injury caused by negligence, fraud or fraudulent misrepresentation, or any other liability that cannot be excluded by law.</p>
        </section>

        <section>
          <h2>9. Third-Party Links</h2>
          <p>The Site may contain links to third-party websites. These links are provided for convenience only. TruthForge does not endorse, control, or accept responsibility for the content or practices of any linked sites.</p>
        </section>

        <section>
          <h2>10. Fees and Payment</h2>
          <p>Fees for Services are agreed in writing as part of each engagement. Unless otherwise agreed:</p>
          <ul>
            <li>Invoices are payable within 30 days of the invoice date</li>
            <li>Late payments will accrue interest at 8% per annum above the Bank of England base rate under the Late Payment of Commercial Debts (Interest) Act 1998</li>
            <li>TruthForge reserves the right to suspend work on an engagement where invoices remain unpaid after the due date</li>
          </ul>
        </section>

        <section>
          <h2>11. Termination</h2>
          <p>Either party may terminate an engagement on written notice in accordance with the terms of the relevant engagement letter. TruthForge reserves the right to terminate or suspend access to the Site or Services immediately if you breach these Terms.</p>
          <p>Termination does not affect accrued rights and liabilities. Clauses 4 (Confidentiality), 5 (Intellectual Property), 7 (Disclaimer), 8 (Limitation of Liability), and 13 (Governing Law) survive termination.</p>
        </section>

        <section>
          <h2>12. Changes to These Terms</h2>
          <p>We may update these Terms from time to time. Changes take effect when posted on the Site. Your continued use of the Site after changes are posted constitutes acceptance of the updated Terms.</p>
          <p>Material changes affecting active engagements will be communicated to clients directly.</p>
        </section>

        <section>
          <h2>13. Governing Law and Jurisdiction</h2>
          <p>These Terms are governed by the laws of England and Wales. Any dispute arising out of or in connection with these Terms or our Services shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
        </section>

        <section>
          <h2>14. Entire Agreement</h2>
          <p>These Terms, together with any engagement letter or statement of work, constitute the entire agreement between you and TruthForge in relation to their subject matter. They supersede all prior representations, agreements, or understandings.</p>
        </section>

        <section>
          <h2>15. Contact Us</h2>
          <p><strong className="text-foreground">TruthForge Limited</strong><br />The Circle, Queen Elizabeth Street<br />London, SE1 2JJ</p>
          <p>Email: <strong className="text-foreground">legal@truthforge.ai</strong></p>
        </section>
      </div>
    </main>
    <Footer />
  </div>
);

export default TermsConditions;